<script setup lang="ts">
import { ref } from "vue";
import { useRouter } from "vue-router";
import PublicHeader from "@/components/public/PublicHeader.vue";
import PublicFooter from "@/components/public/PublicFooter.vue";

const router = useRouter();

const handleHelpClick = () => {
  console.log("Help button clicked");
};

const handleLanguageChange = (language: string) => {
  console.log("Language changed to:", language);
};

const handleFooterLinkClick = (section: string, item: string) => {
  console.log("Footer link clicked:", section, item);
};

const handleRegister = () => {
  console.log("Auditor registration initiated");
};
</script>

<template>
  <PublicHeader
    @help-click="handleHelpClick"
    @language-change="handleLanguageChange"
  />

  <main id="main-content">
    <section
      id="breadcrumb-section"
      class="bg-white border-b border-gray-200 py-4"
    >
      <div class="max-w-7xl mx-auto px-5">
        <div class="flex items-center space-x-2 text-sm text-gray-600">
          <span class="hover:text-primary cursor-pointer">{{ $t('auditor.register_page.breadcrumb.home') }}</span>
          <i class="fas fa-chevron-right text-xs"></i>
          <span class="hover:text-primary cursor-pointer">{{ $t('auditor.register_page.breadcrumb.registration') }}</span>
          <i class="fas fa-chevron-right text-xs"></i>
          <span class="text-accent font-medium">{{ $t('auditor.register_page.breadcrumb.auditor_registration') }}</span>
        </div>
      </div>
    </section>

    <section
      id="registration-hero"
      class="bg-gradient-to-r from-secondary/10 to-primary/10 py-8"
    >
      <div class="max-w-4xl mx-auto px-5 text-center">
        <div
          class="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-4"
        >
          <i class="fas fa-clipboard-check text-secondary text-2xl"></i>
        </div>
        <h1 class="text-3xl font-bold text-accent mb-3">
          {{ $t('auditor.register_page.hero.title') }}
        </h1>
        <p class="text-lg text-gray-600 mb-2">
          {{ $t('auditor.register_page.hero.subtitle') }}
        </p>
        <div
          class="inline-flex items-center bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium"
        >
          <i class="fas fa-dollar-sign mr-2"></i>
          {{ $t('auditor.register_page.hero.registration_fee') }}
        </div>
      </div>
    </section>

    <section id="registration-form-section" class="py-12">
      <div class="max-w-4xl mx-auto px-5">
        <div class="bg-white rounded-lg border border-gray-200 shadow-sm">
          <div id="form-header" class="border-b border-gray-200 p-6">
            <div class="flex items-center justify-between">
              <div>
                <h2 class="text-xl font-semibold text-accent">
                  {{ $t('auditor.register_page.form.title') }}
                </h2>
                <p class="text-gray-600 text-sm mt-1">
                  {{ $t('auditor.register_page.form.subtitle') }}
                </p>
              </div>
              <div class="text-right">
                <div class="text-sm text-gray-500">{{ $t('auditor.register_page.form.step_progress') }}</div>
                <div class="w-32 bg-gray-200 rounded-full h-2 mt-1">
                  <div class="bg-primary h-2 rounded-full w-1/3"></div>
                </div>
              </div>
            </div>
          </div>

          <form id="auditor-registration-form" class="p-6">
            <div id="entity-information" class="mb-8">
              <h3
                class="text-lg font-semibold text-accent mb-4 flex items-center"
              >
                <i class="fas fa-building text-primary mr-2"></i>
                {{ $t('auditor.register_page.form.entity_information.title') }}
              </h3>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.entity_information.entity_name') }} *</label
                  >
                  <input
                    type="text"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    :placeholder="$t('auditor.register_page.form.entity_information.entity_name_placeholder')"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.entity_information.country') }} *</label
                  >
                  <select
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="">{{ $t('auditor.register_page.form.entity_information.select_country') }}</option>
                    <option value="US">United States</option>
                    <option value="CA">Canada</option>
                    <option value="UK">United Kingdom</option>
                    <option value="DE">Germany</option>
                    <option value="FR">France</option>
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.entity_information.registration_number') }} *</label
                  >
                  <input
                    type="text"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    :placeholder="$t('auditor.register_page.form.entity_information.registration_number_placeholder')"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.entity_information.contact_email') }} *</label
                  >
                  <input
                    type="email"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    :placeholder="$t('auditor.register_page.form.entity_information.contact_email_placeholder')"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.entity_information.phone_number') }} *</label
                  >
                  <input
                    type="tel"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    :placeholder="$t('auditor.register_page.form.entity_information.phone_number_placeholder')"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.entity_information.website') }}</label
                  >
                  <input
                    type="url"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    :placeholder="$t('auditor.register_page.form.entity_information.website_placeholder')"
                  />
                </div>
              </div>

              <div class="mt-6">
                <label class="block text-sm font-medium text-gray-700 mb-2"
                  >{{ $t('auditor.register_page.form.entity_information.address') }} *</label
                >
                <textarea
                  class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  rows="3"
                  :placeholder="$t('auditor.register_page.form.entity_information.address_placeholder')"
                ></textarea>
              </div>
            </div>

            <div id="accreditation-details" class="mb-8">
              <h3
                class="text-lg font-semibold text-accent mb-4 flex items-center"
              >
                <i class="fas fa-certificate text-primary mr-2"></i>
                {{ $t('auditor.register_page.form.accreditation_details.title') }}
              </h3>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.accreditation_details.accreditation_body') }} *</label
                  >
                  <select
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="">{{ $t('auditor.register_page.form.accreditation_details.select_body') }}</option>
                    <option value="IAF">
                      International Accreditation Forum (IAF)
                    </option>
                    <option value="ANAB">
                      ANSI National Accreditation Board
                    </option>
                    <option value="UKAS">
                      United Kingdom Accreditation Service
                    </option>
                    <option value="DAkkS">
                      Deutsche Akkreditierungsstelle
                    </option>
                    <option value="COFRAC">
                      Comité français d'accréditation
                    </option>
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.accreditation_details.accreditation_number') }} *</label
                  >
                  <input
                    type="text"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                    :placeholder="$t('auditor.register_page.form.accreditation_details.accreditation_number_placeholder')"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.accreditation_details.valid_until') }} *</label
                  >
                  <input
                    type="date"
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.accreditation_details.iso_standard') }} *</label
                  >
                  <select
                    class="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  >
                    <option value="">{{ $t('auditor.register_page.form.accreditation_details.select_iso') }}</option>
                    <option value="ISO14065">ISO 14065:2020</option>
                    <option value="ISO17029">ISO/IEC 17029:2019</option>
                    <option value="ISO19011">ISO 19011:2018</option>
                  </select>
                </div>
              </div>
            </div>

            <div id="sectoral-scope" class="mb-8">
              <h3
                class="text-lg font-semibold text-accent mb-4 flex items-center"
              >
                <i class="fas fa-list-check text-primary mr-2"></i>
                {{ $t('auditor.register_page.form.sectoral_scope.title') }}
              </h3>

              <div
                class="bg-blue-50 border border-blue-200 rounded-md p-4 mb-4"
              >
                <div class="flex items-start space-x-2">
                  <i class="fas fa-info-circle text-blue-600 mt-0.5"></i>
                  <div class="text-sm text-blue-800">
                    <p class="font-medium mb-1">
                      {{ $t('auditor.register_page.form.sectoral_scope.info_title') }}
                    </p>
                    <p>
                      {{ $t('auditor.register_page.form.sectoral_scope.info_description') }}
                    </p>
                  </div>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <label
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div>
                    <div class="font-medium text-sm">{{ $t('auditor.register_page.form.sectoral_scope.energy_renewable') }}</div>
                    <div class="text-xs text-gray-500">{{ $t('auditor.register_page.form.sectoral_scope.energy_renewable_desc') }}</div>
                  </div>
                </label>

                <label
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div>
                    <div class="font-medium text-sm">{{ $t('auditor.register_page.form.sectoral_scope.energy_efficiency') }}</div>
                    <div class="text-xs text-gray-500">{{ $t('auditor.register_page.form.sectoral_scope.energy_efficiency_desc') }}</div>
                  </div>
                </label>

                <label
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div>
                    <div class="font-medium text-sm">{{ $t('auditor.register_page.form.sectoral_scope.transport') }}</div>
                    <div class="text-xs text-gray-500">
                      {{ $t('auditor.register_page.form.sectoral_scope.transport_desc') }}
                    </div>
                  </div>
                </label>

                <label
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div>
                    <div class="font-medium text-sm">{{ $t('auditor.register_page.form.sectoral_scope.manufacturing') }}</div>
                    <div class="text-xs text-gray-500">
                      {{ $t('auditor.register_page.form.sectoral_scope.manufacturing_desc') }}
                    </div>
                  </div>
                </label>

                <label
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div>
                    <div class="font-medium text-sm">Chemical Industry</div>
                    <div class="text-xs text-gray-500">Process emissions</div>
                  </div>
                </label>

                <label
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div>
                    <div class="font-medium text-sm">Construction</div>
                    <div class="text-xs text-gray-500">Materials, Methods</div>
                  </div>
                </label>

                <label
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div>
                    <div class="font-medium text-sm">Mining/Mineral</div>
                    <div class="text-xs text-gray-500">
                      Extraction, Processing
                    </div>
                  </div>
                </label>

                <label
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div>
                    <div class="font-medium text-sm">Metal Production</div>
                    <div class="text-xs text-gray-500">Steel, Aluminum</div>
                  </div>
                </label>

                <label
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div>
                    <div class="font-medium text-sm">Fugitive Emissions</div>
                    <div class="text-xs text-gray-500">Oil, Gas, Coal</div>
                  </div>
                </label>

                <label
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div>
                    <div class="font-medium text-sm">Solvents</div>
                    <div class="text-xs text-gray-500">Industrial use</div>
                  </div>
                </label>

                <label
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div>
                    <div class="font-medium text-sm">Waste Management</div>
                    <div class="text-xs text-gray-500">Disposal, Treatment</div>
                  </div>
                </label>

                <label
                  class="flex items-start space-x-3 p-3 border border-gray-200 rounded-md hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    class="mt-1 text-primary focus:ring-primary border-gray-300 rounded"
                  />
                  <div>
                    <div class="font-medium text-sm">AFOLU</div>
                    <div class="text-xs text-gray-500">
                      Agriculture, Forestry
                    </div>
                  </div>
                </label>
              </div>
            </div>

            <div id="document-uploads" class="mb-8">
              <h3
                class="text-lg font-semibold text-accent mb-4 flex items-center"
              >
                <i class="fas fa-upload text-primary mr-2"></i>
                {{ $t('auditor.register_page.form.documents.title') }}
              </h3>

              <div class="space-y-4">
                <div class="border border-gray-200 rounded-md p-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.documents.accreditation_certificate') }} *</label
                  >
                  <div
                    class="border-2 border-dashed border-gray-300 rounded-md p-6 text-center hover:border-primary transition-colors cursor-pointer"
                  >
                    <i
                      class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                    ></i>
                    <p class="text-sm text-gray-600">
                      {{ $t('auditor.register_page.form.documents.upload_instructions') }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">{{ $t('auditor.register_page.form.documents.file_requirements') }}</p>
                  </div>
                </div>

                <div class="border border-gray-200 rounded-md p-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.documents.risk_framework') }} *</label
                  >
                  <div
                    class="border-2 border-dashed border-gray-300 rounded-md p-6 text-center hover:border-primary transition-colors cursor-pointer"
                  >
                    <i
                      class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                    ></i>
                    <p class="text-sm text-gray-600">
                      {{ $t('auditor.register_page.form.documents.upload_instructions') }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">{{ $t('auditor.register_page.form.documents.file_requirements') }}</p>
                  </div>
                </div>

                <div class="border border-gray-200 rounded-md p-4">
                  <label class="block text-sm font-medium text-gray-700 mb-2"
                    >{{ $t('auditor.register_page.form.documents.declaration_impartiality') }} *</label
                  >
                  <div
                    class="border-2 border-dashed border-gray-300 rounded-md p-6 text-center hover:border-primary transition-colors cursor-pointer"
                  >
                    <i
                      class="fas fa-cloud-upload-alt text-gray-400 text-2xl mb-2"
                    ></i>
                    <p class="text-sm text-gray-600">
                      {{ $t('auditor.register_page.form.documents.upload_instructions') }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">{{ $t('auditor.register_page.form.documents.file_requirements') }}</p>
                  </div>
                </div>
              </div>
            </div>

            <div id="declarations" class="mb-8">
              <h3
                class="text-lg font-semibold text-accent mb-4 flex items-center"
              >
                <i class="fas fa-check-square text-primary mr-2"></i>
                {{ $t('auditor.register_page.form.declarations.title') }}
              </h3>
            </div>
          </form>
        </div>
      </div>
    </section>
  </main>
  <PublicFooter @link-click="handleFooterLinkClick" />
</template>
